import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateAssetComparisonQuery } from '~/components/AssetModalV2/comparison/getPrivateAssetComparisonQuery';
import { parsePrivateAssetComparisonURLState } from '~/components/AssetModalV2/comparison/parsePrivateAssetComparisonURLState';
import { createPrivateAssetComparisonURLMutationController } from '~/components/AssetModalV2/comparison/utils/createPrivateAssetComparisonURLMutationController';
import {
  type ComparisonActivePanel,
  type ComparisonMode,
  DEFAULT_COMPARISON_LINKED,
  EXIT_COMPARISON_QUERY,
  getEnterAssetComparisonQuery,
  type PrivateAssetComparisonURLState,
} from '~/constants/assetComparison';
import { pushWithExistingQuery } from '~/utils/PathUtils';

/** Next Router cancels overlapping replaces, so every hook instance shares one mutation queue. */
const comparisonURLMutationController = createPrivateAssetComparisonURLMutationController(DEFAULT_COMPARISON_LINKED);

const reportComparisonURLMutationError = (error: unknown, action: string) => {
  reportErrorToBugsnag({
    error,
    context: 'Failed to update private asset comparison URL state',
    metadata: { comparisonURLMutation: { action } },
  });
};

const consumeComparisonURLMutation = (mutation: Promise<unknown>, action: string) => {
  void mutation.catch((error: unknown) => reportComparisonURLMutationError(error, action));
};

type ComparisonNavigation = () => Promise<unknown> | unknown;

/** Reads and writes the private asset comparison's shareable URL state. */
export const usePrivateAssetComparisonURLState = () => {
  const { query } = useRouter();
  const comparisonState = useMemo(() => parsePrivateAssetComparisonURLState(query), [query]);

  useEffect(() => {
    comparisonURLMutationController.reconcileURLState({
      comparisonIsOpen: comparisonState.comparisonClipId !== undefined,
      isLinked: comparisonState.isLinked,
    });
  }, [comparisonState.comparisonClipId, comparisonState.isLinked]);

  const updateComparisonQuery = useCallback((update: Partial<PrivateAssetComparisonURLState>) => {
    consumeComparisonURLMutation(
      comparisonURLMutationController.enqueueRegular(() =>
        pushWithExistingQuery({ newQuery: getPrivateAssetComparisonQuery(update), method: 'replace' }),
      ),
      'update comparison query',
    );
  }, []);

  const startComparison = useCallback((otherClipId: string) => {
    consumeComparisonURLMutation(
      comparisonURLMutationController.enqueueStart(() =>
        pushWithExistingQuery({
          newQuery: { ...EXIT_COMPARISON_QUERY, ...getEnterAssetComparisonQuery(otherClipId) },
        }),
      ),
      'start comparison',
    );
  }, []);

  const exitComparison = useCallback(
    () =>
      comparisonURLMutationController
        .enqueueExit(() => pushWithExistingQuery({ newQuery: EXIT_COMPARISON_QUERY, method: 'replace' }))
        .then(() => undefined),
    [],
  );

  const runComparisonNavigation = useCallback(
    (navigate: ComparisonNavigation) => comparisonURLMutationController.enqueueRegular(navigate).then(() => undefined),
    [],
  );

  const exitComparisonWithNavigation = useCallback(
    (navigate: ComparisonNavigation) =>
      comparisonURLMutationController.enqueueExitWithNavigation(navigate).then(() => undefined),
    [],
  );

  const setMode = useCallback((mode: ComparisonMode) => updateComparisonQuery({ mode }), [updateComparisonQuery]);

  const setComparisonClipId = useCallback(
    (comparisonClipId: string) => updateComparisonQuery({ comparisonClipId }),
    [updateComparisonQuery],
  );

  const setActivePanel = useCallback(
    (activePanel: ComparisonActivePanel) => updateComparisonQuery({ activePanel }),
    [updateComparisonQuery],
  );

  const toggleLinked = useCallback(() => {
    consumeComparisonURLMutation(
      comparisonURLMutationController.enqueueToggleLinked(comparisonState.isLinked, (nextIsLinked) =>
        pushWithExistingQuery({
          newQuery: getPrivateAssetComparisonQuery({ isLinked: nextIsLinked }),
          method: 'replace',
        }),
      ),
      'toggle linked playback',
    );
  }, [comparisonState.isLinked]);

  const setSliderPosition = useCallback(
    (sliderPosition: number) => updateComparisonQuery({ sliderPosition }),
    [updateComparisonQuery],
  );

  return {
    ...comparisonState,
    exitComparison,
    exitComparisonWithNavigation,
    runComparisonNavigation,
    setActivePanel,
    setComparisonClipId,
    setMode,
    setSliderPosition,
    startComparison,
    toggleLinked,
  };
};
